<div [class]="sectionClasses" [class.placeholder-glow]="isLoading">
	<div class="coa-category-header d-flex justify-content-between align-items-baseline flex-wrap gap-2">
		<h5 class="fw-semibold mb-0">
			{{ category() | formatLabel }}
			<span class="text-muted small fw-normal">({{ range().min }}&ndash;{{ range().max }})</span>
		</h5>

		<button *ifHasPermission="{requiredPermissions: [ChartOfAccountsPermission.Create], userPermissionOnly: false, dataEntityType: DataEntityTypes.User}"
			type="button" class="btn btn-sm btn-primary" (click)="openAddModal()">
			<fa-duotone-icon icon="plus" secondaryOpacity="0.9" class="me-1" />
			Add Account
		</button>
	</div>

	<!-- Desktop / tablet: full table -->
	<div class="table-responsive coa-section-table d-none d-md-block">
		<table msResponsiveTable class="table table-hover fixed-header align-middle mb-0">
			<thead>
				<tr>
					<th msResponsiveTableColumn="accountNumber" [priority]="2" scope="col" class="numeric-column">Account #</th>
					<th msResponsiveTableColumn="name" [priority]="1" scope="col">Name</th>
					<th msResponsiveTableColumn="description" [priority]="4" scope="col">Description</th>
					<th msResponsiveTableColumn="status" [priority]="3" scope="col">Status</th>
					<th msResponsiveTableColumn="actions" [priority]="1" scope="col">Actions</th>
				</tr>
			</thead>
			<tbody>
				@if (isLoading && accounts().length === 0) {
					@for (row of skeletonRows; track row) {
						<tr class="placeholder-glow">
							<td class="numeric-column"><span class="placeholder col-6"></span></td>
							<td><span class="placeholder col-8"></span></td>
							<td><span class="placeholder col-10"></span></td>
							<td><span class="placeholder col-4"></span></td>
							<td><span class="placeholder col-6"></span></td>
						</tr>
					}
				}

				@if (!isLoading && accounts().length === 0) {
					<tr>
						<td colspan="5" class="text-muted text-center py-3">No accounts in this category.</td>
					</tr>
				}

				@for (account of accounts(); track account.chartOfAccountId) {
					@if (editingId() === account.chartOfAccountId) {
						<tr [formGroup]="editForm">
							<td msResponsiveTableColumn="accountNumber" [priority]="2" class="numeric-column">
								<input type="number" class="form-control form-control-sm text-end" formControlName="accountNumber"
									[class.is-invalid]="editForm.get('accountNumber')?.invalid && editForm.get('accountNumber')?.touched"
									[min]="range().min" [max]="range().max" />
								@if (getAccountNumberError(editForm); as error) {
									<div class="text-danger small mt-1 text-start">{{ error }}</div>
								}
							</td>
							<td msResponsiveTableColumn="name" [priority]="1">
								<input type="text" class="form-control form-control-sm" formControlName="name"
									[class.is-invalid]="editForm.get('name')?.invalid && editForm.get('name')?.touched" />
								@if (getNameError(editForm); as error) {
									<div class="text-danger small mt-1">{{ error }}</div>
								}
							</td>
							<td msResponsiveTableColumn="description" [priority]="4">
								<input type="text" class="form-control form-control-sm" formControlName="description"
									[class.is-invalid]="editForm.get('description')?.invalid && editForm.get('description')?.touched" />
								@if (getDescriptionError(editForm); as error) {
									<div class="text-danger small mt-1">{{ error }}</div>
								}
							</td>
							<td msResponsiveTableColumn="status" [priority]="3">
								<span class="badge" [class.bg-success-subtle]="account.isActive" [class.text-success]="account.isActive"
									[class.bg-warning-subtle]="!account.isActive" [class.text-warning-emphasis]="!account.isActive">
									{{ account.isActive ? 'Active' : 'Retired' }}
								</span>
							</td>
							<td msResponsiveTableColumn="actions" [priority]="1">
								<div class="d-flex coa-action-buttons">
									<button type="button" class="btn btn-sm btn-outline-danger" [disabled]="isSavingRow()" (click)="cancelEdit()" title="Cancel">
										<fa-duotone-icon icon="xmark" secondaryOpacity="0.9" />
									</button>
									<button type="button" class="btn btn-sm btn-outline-success" [disabled]="isSavingRow()" (click)="saveEdit(account)" title="Save">
										@if (isSavingRow()) {
											<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
										} @else {
											<fa-duotone-icon icon="check" secondaryOpacity="0.9" />
										}
									</button>
								</div>
							</td>
						</tr>
					} @else {
						<tr>
							<td msResponsiveTableColumn="accountNumber" [priority]="2" class="numeric-column">{{ account.accountNumber }}</td>
							<td msResponsiveTableColumn="name" [priority]="1">
								<span class="text-truncate d-block" [msTooltip]="account.name" msTooltipOnlyIfTruncated>{{ account.name }}</span>
							</td>
							<td msResponsiveTableColumn="description" [priority]="4">
								<span class="text-truncate d-block text-muted" [msTooltip]="account.description ?? ''" msTooltipOnlyIfTruncated>
									{{ account.description | noValue }}
								</span>
							</td>
							<td msResponsiveTableColumn="status" [priority]="3">
								<span class="badge" [class.bg-success-subtle]="account.isActive" [class.text-success]="account.isActive"
									[class.bg-warning-subtle]="!account.isActive" [class.text-warning-emphasis]="!account.isActive">
									{{ account.isActive ? 'Active' : 'Retired' }}
								</span>
							</td>
							<td msResponsiveTableColumn="actions" [priority]="1">
								<div class="dropdown">
									<button class="btn btn-sm btn-ms-secondary dropdown-toggle" data-bs-toggle="dropdown">
										Actions
									</button>
									<ul class="dropdown-menu dropdown-menu-end">
										<li *ifHasPermission="{requiredPermissions: [ChartOfAccountsPermission.Update], userPermissionOnly: false, dataEntityType: DataEntityTypes.User}">
											<a class="dropdown-item" (click)="startEdit(account)">
												<fa-duotone-icon icon="pen-to-square" secondaryOpacity="0.3" class="me-2" />Edit
											</a>
										</li>
										<li *ifHasPermission="{requiredPermissions: [ChartOfAccountsPermission.Update], userPermissionOnly: false, dataEntityType: DataEntityTypes.User}">
											<a class="dropdown-item" (click)="confirmStatusChange(account)">
												<fa-duotone-icon [icon]="account.isActive ? 'box-archive' : 'rotate-left'" secondaryOpacity="0.3" class="me-2" />
												{{ account.isActive ? 'Retire' : 'Reactivate' }}
											</a>
										</li>
									</ul>
								</div>
							</td>
						</tr>
					}
				}
			</tbody>
		</table>
		@if (hasNext) {
			<div ms-in-view-port (inViewPort)="loadMoreInView()" class="coa-scroll-sentinel"></div>
		}
	</div>

	<!-- Mobile: dense cards -->
	<div class="d-md-none">
		@if (isLoading && accounts().length === 0) {
			@for (row of skeletonRows; track row) {
				<div class="card my-3 placeholder-glow">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-baseline gap-2">
							<span class="placeholder col-6"></span>
							<span class="placeholder col-2"></span>
						</div>
						<span class="placeholder col-8 mt-1"></span>
					</div>
				</div>
			}
		}

		@if (!isLoading && accounts().length === 0) {
			<p class="text-muted text-center py-3 mb-0">No accounts in this category.</p>
		}

		@for (account of accounts(); track account.chartOfAccountId) {
			@if (editingId() === account.chartOfAccountId) {
				<div class="card my-3" [formGroup]="editForm">
					<div class="card-body">
						<label class="form-label small text-muted mb-1">Account #</label>
						<input type="number" class="form-control form-control-sm mb-2" formControlName="accountNumber"
							[class.is-invalid]="editForm.get('accountNumber')?.invalid && editForm.get('accountNumber')?.touched"
							[min]="range().min" [max]="range().max" />
						@if (getAccountNumberError(editForm); as error) {
							<div class="text-danger small mb-2">{{ error }}</div>
						}

						<label class="form-label small text-muted mb-1">Name</label>
						<input type="text" class="form-control form-control-sm mb-2" formControlName="name"
							[class.is-invalid]="editForm.get('name')?.invalid && editForm.get('name')?.touched" />
						@if (getNameError(editForm); as error) {
							<div class="text-danger small mb-2">{{ error }}</div>
						}

						<label class="form-label small text-muted mb-1">Description</label>
						<input type="text" class="form-control form-control-sm" formControlName="description"
							[class.is-invalid]="editForm.get('description')?.invalid && editForm.get('description')?.touched" />
						@if (getDescriptionError(editForm); as error) {
							<div class="text-danger small mb-2">{{ error }}</div>
						}
					</div>
					<div class="d-flex coa-mobile-footer">
						<button type="button" class="btn bg-danger-subtle text-danger flex-fill border-0 coa-mobile-footer-btn" [disabled]="isSavingRow()" (click)="cancelEdit()">
							Cancel
						</button>
						<button type="button" class="btn bg-success-subtle text-success flex-fill border-0 coa-mobile-footer-btn" [disabled]="isSavingRow()" (click)="saveEdit(account)">
							@if (isSavingRow()) {
								<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							} @else {
								Save
							}
						</button>
					</div>
				</div>
			} @else {
				<div class="card my-3">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-baseline gap-2">
							<div class="fw-semibold text-truncate" [msTooltip]="account.accountNumber + ' - ' + account.name" msTooltipOnlyIfTruncated>
								{{ account.accountNumber }} - {{ account.name }}
							</div>
							<span class="badge text-nowrap" [class.bg-success-subtle]="account.isActive" [class.text-success]="account.isActive"
								[class.bg-warning-subtle]="!account.isActive" [class.text-warning-emphasis]="!account.isActive">
								{{ account.isActive ? 'Active' : 'Retired' }}
							</span>
						</div>
						@if (account.description) {
							<div class="text-muted small">{{ account.description }}</div>
						}
					</div>
					<button class="mobile-action-button dropdown-toggle" data-bs-toggle="dropdown">
						Actions
					</button>
					<ul class="dropdown-menu dropdown-menu-end w-75">
						<li *ifHasPermission="{requiredPermissions: [ChartOfAccountsPermission.Update], userPermissionOnly: false, dataEntityType: DataEntityTypes.User}">
							<a class="dropdown-item" (click)="startEdit(account)">
								<fa-duotone-icon icon="pen-to-square" secondaryOpacity="0.3" class="me-2" />Edit
							</a>
						</li>
						<li *ifHasPermission="{requiredPermissions: [ChartOfAccountsPermission.Update], userPermissionOnly: false, dataEntityType: DataEntityTypes.User}">
							<a class="dropdown-item" (click)="confirmStatusChange(account)">
								<fa-duotone-icon [icon]="account.isActive ? 'box-archive' : 'rotate-left'" secondaryOpacity="0.3" class="me-2" />
								{{ account.isActive ? 'Retire' : 'Reactivate' }}
							</a>
						</li>
					</ul>
				</div>
			}
		}
		@if (hasNext) {
			<div ms-in-view-port (inViewPort)="loadMoreInView()" class="coa-scroll-sentinel"></div>
		}
	</div>
</div>

<!-- Add Account modal — category is implicit (this section's own category), never a field to pick -->
<div class="modal fade" [id]="addModalId" msEscClose (escPressed)="cancelAdd()"
	data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
	<div class="modal-dialog modal-dialog-centered">
		<div class="modal-content" [formGroup]="addForm">
			<div class="modal-header border-0 p-4">
				<h5 class="modal-title">Add {{ category() | formatLabel }} Account</h5>
			</div>
			<div class="modal-body">
				@if (rowError()) {
					<p class="text-danger small">{{ rowError() }}</p>
				}

				<label class="form-label small text-muted mb-1">Account #</label>
				<input type="number" class="form-control mb-2" formControlName="accountNumber"
					[class.is-invalid]="addForm.get('accountNumber')?.invalid && addForm.get('accountNumber')?.touched"
					[min]="range().min" [max]="range().max" placeholder="{{ range().min }}-{{ range().max }}" />
				@if (getAccountNumberError(addForm); as error) {
					<div class="text-danger small mb-2">{{ error }}</div>
				}

				<label class="form-label small text-muted mb-1">Name</label>
				<input type="text" class="form-control mb-2" formControlName="name" placeholder="Account name"
					[class.is-invalid]="addForm.get('name')?.invalid && addForm.get('name')?.touched" />
				@if (getNameError(addForm); as error) {
					<div class="text-danger small mb-2">{{ error }}</div>
				}

				<label class="form-label small text-muted mb-1">Description</label>
				<input type="text" class="form-control" formControlName="description" placeholder="Optional"
					[class.is-invalid]="addForm.get('description')?.invalid && addForm.get('description')?.touched" />
				@if (getDescriptionError(addForm); as error) {
					<div class="text-danger small mb-2">{{ error }}</div>
				}
			</div>
			<div class="modal-footer border-0 d-flex justify-content-center pb-4">
				<button type="button" class="btn btn-ms-secondary" [disabled]="isSavingRow()" (click)="cancelAdd()">
					Cancel
				</button>
				<button type="button" class="btn btn-primary" [disabled]="isSavingRow()" (click)="saveAdd()">
					@if (isSavingRow()) {
						<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
					} @else {
						Save
					}
				</button>
			</div>
		</div>
	</div>
</div>

<ms-confirmation-modal #retireConfirmationModalRef [modalId]="retireModalId" [(isSubmitting)]="isSubmittingStatusChange"
	(confirmed)="performStatusChange($event)" (cancelled)="cancelStatusChange()" />

<ms-info-modal #infoModalRef [modalId]="infoModalId" />
